import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { SavedPlot, PlotInput, PlotResult, PlotChapter } from '@creanizer/types'
import { createIndexedDBStorage } from '@creanizer/utils'

interface PlotStore {
  savedPlots: SavedPlot[]
  maxPlots: number
  
  // アクション
  savePlot: (input: PlotInput, result: PlotResult) => string | null
  deletePlot: (id: string) => void
  getPlot: (id: string) => SavedPlot | undefined
  updatePlotMemo: (id: string, memo: string) => void
  updatePlotTags: (id: string, tags: string[]) => void
  exportPlot: (id: string) => void
  exportAsText: () => void
  exportAsJSON: () => void
  importFromJSON: (jsonString: string) => void
  clearAllPlots: () => void
  isPlotSaved: (title: string) => boolean
  
  // 編集機能
  updatePlot: (id: string, result: PlotResult) => void
  updateChapter: (plotId: string, chapterNumber: number, chapter: PlotChapter) => void
  addChapter: (plotId: string, afterNumber: number, chapter: PlotChapter) => void
  deleteChapter: (plotId: string, chapterNumber: number) => void
  updatePlotField: (plotId: string, field: keyof PlotResult, value: any) => void
}

export const usePlotStore = create<PlotStore>()(
  persist(
    (set, get) => ({
      savedPlots: [],
      maxPlots: 500,

      savePlot: (input: PlotInput, result: PlotResult) => {
        const { savedPlots, maxPlots } = get()
        
        // 既に保存されているかチェック
        const existing = savedPlots.find(p => p.title === result.title)
        if (existing) {
          return existing.id
        }
        
        // 最大保存数チェック
        if (savedPlots.length >= maxPlots) {
          alert(`保存数が上限（${maxPlots}件）に達しています。不要なプロットを削除してください。`)
          return null
        }

        const newPlot: SavedPlot = {
          id: crypto.randomUUID(),
          title: result.title,
          input,
          result,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }

        set((state) => ({
          savedPlots: [...state.savedPlots, newPlot],
        }))

        return newPlot.id
      },

      deletePlot: (id: string) => {
        set((state) => ({
          savedPlots: state.savedPlots.filter((plot) => plot.id !== id),
        }))
      },

      getPlot: (id: string) => {
        return get().savedPlots.find((plot) => plot.id === id)
      },

      updatePlotMemo: (id: string, memo: string) => {
        set((state) => ({
          savedPlots: state.savedPlots.map((plot) =>
            plot.id === id
              ? { ...plot, memo, updatedAt: new Date().toISOString() }
              : plot
          ),
        }))
      },

      updatePlotTags: (id: string, tags: string[]) => {
        set((state) => ({
          savedPlots: state.savedPlots.map((plot) =>
            plot.id === id
              ? { ...plot, tags, updatedAt: new Date().toISOString() }
              : plot
          ),
        }))
      },

      exportPlot: (id: string) => {
        const plot = get().getPlot(id)
        if (!plot) return

        const content = formatPlotForExport(plot)
        downloadTextFile(content, `plot_${plot.title}_${Date.now()}.txt`)
      },

      exportAsText: () => {
        const { savedPlots } = get()
        if (savedPlots.length === 0) {
          alert('保存されたプロットがありません。')
          return
        }

        const content = savedPlots
          .map((plot) => formatPlotForExport(plot))
          .join('\n\n' + '='.repeat(80) + '\n\n')

        downloadTextFile(content, `all_plots_${Date.now()}.txt`)
      },

      exportAsJSON: () => {
        const { savedPlots } = get()
        if (savedPlots.length === 0) {
          alert('保存されたプロットがありません。')
          return
        }

        const exportData = {
          version: '1.0.0',
          exportDate: new Date().toISOString(),
          dataType: 'plots',
          data: savedPlots
        }

        downloadJSONFile(exportData, `saved_plots_${Date.now()}.json`)
      },

      importFromJSON: (jsonString: string) => {
        try {
          const importData = JSON.parse(jsonString)
          
          // バリデーション
          if (!importData.data || !Array.isArray(importData.data)) {
            throw new Error('無効なデータ形式です')
          }

          const { maxPlots, savedPlots } = get()
          const newPlots = importData.data as SavedPlot[]
          
          // 重複チェック（タイトルとあらすじで判定）
          const uniquePlots = newPlots.filter(newPlot => {
            return !savedPlots.some(existing => 
              existing.title === newPlot.title && 
              existing.result.synopsis === newPlot.result.synopsis
            )
          })
          
          // マージ後の最大数チェック
          const totalCount = savedPlots.length + uniquePlots.length
          if (totalCount > maxPlots) {
            const availableSlots = maxPlots - savedPlots.length
            if (availableSlots <= 0) {
              alert(`保存数が上限（${maxPlots}件）に達しています。不要なプロットを削除してください。`)
              return
            }
            if (!confirm(`追加可能な件数は${availableSlots}件です。${uniquePlots.length}件中${availableSlots}件のみインポートしますか？`)) {
              return
            }
            uniquePlots.splice(availableSlots)
          }

          // IDの重複を避けるため、新しいIDを生成して追加
          const importedPlots = uniquePlots.map(plot => ({
            ...plot,
            id: crypto.randomUUID(),
            createdAt: plot.createdAt || new Date().toISOString(),
            updatedAt: plot.updatedAt || new Date().toISOString()
          }))
          
          const mergedPlots = [...savedPlots, ...importedPlots]

          set({ savedPlots: mergedPlots })
          
          const skippedCount = newPlots.length - uniquePlots.length
          alert(`${importedPlots.length}件のプロットをインポートしました。${skippedCount > 0 ? `(${skippedCount}件の重複をスキップ)` : ''}`)
        } catch (error) {
          console.error('Import error:', error)
          alert('インポートに失敗しました。ファイル形式を確認してください。')
        }
      },

      clearAllPlots: () => {
        if (confirm('保存されているすべてのプロットを削除しますか？')) {
          set({ savedPlots: [] })
        }
      },

      isPlotSaved: (title: string) => {
        return get().savedPlots.some(plot => plot.title === title)
      },

      // 編集機能の実装
      updatePlot: (id: string, result: PlotResult) => {
        set((state) => ({
          savedPlots: state.savedPlots.map((plot) =>
            plot.id === id
              ? { ...plot, result, updatedAt: new Date().toISOString() }
              : plot
          ),
        }))
      },

      updateChapter: (plotId: string, chapterNumber: number, chapter: PlotChapter) => {
        set((state) => ({
          savedPlots: state.savedPlots.map((plot) => {
            if (plot.id !== plotId) return plot
            
            const newChapters = [...plot.result.structure]
            const chapterIndex = newChapters.findIndex(ch => ch.number === chapterNumber)
            if (chapterIndex !== -1) {
              newChapters[chapterIndex] = chapter
            }
            
            return {
              ...plot,
              result: {
                ...plot.result,
                structure: newChapters
              },
              updatedAt: new Date().toISOString()
            }
          }),
        }))
      },

      addChapter: (plotId: string, afterNumber: number, chapter: PlotChapter) => {
        set((state) => ({
          savedPlots: state.savedPlots.map((plot) => {
            if (plot.id !== plotId) return plot
            
            const newChapters = [...plot.result.structure]
            const insertIndex = newChapters.findIndex(ch => ch.number === afterNumber) + 1
            
            // afterNumberより後の章番号を更新
            const updatedChapters = newChapters.map(ch => {
              if (ch.number > afterNumber) {
                return { ...ch, number: ch.number + 1 }
              }
              return ch
            })
            
            // 新しい章を挿入
            updatedChapters.splice(insertIndex, 0, {
              ...chapter,
              number: afterNumber + 1
            })
            
            return {
              ...plot,
              result: {
                ...plot.result,
                structure: updatedChapters
              },
              updatedAt: new Date().toISOString()
            }
          }),
        }))
      },

      deleteChapter: (plotId: string, chapterNumber: number) => {
        set((state) => ({
          savedPlots: state.savedPlots.map((plot) => {
            if (plot.id !== plotId) return plot
            
            const newChapters = plot.result.structure
              .filter(ch => ch.number !== chapterNumber)
              .map(ch => {
                // 削除した章より後の章番号を調整
                if (ch.number > chapterNumber) {
                  return { ...ch, number: ch.number - 1 }
                }
                return ch
              })
            
            return {
              ...plot,
              result: {
                ...plot.result,
                structure: newChapters
              },
              updatedAt: new Date().toISOString()
            }
          }),
        }))
      },

      updatePlotField: (plotId: string, field: keyof PlotResult, value: any) => {
        set((state) => ({
          savedPlots: state.savedPlots.map((plot) => {
            if (plot.id !== plotId) return plot
            
            return {
              ...plot,
              result: {
                ...plot.result,
                [field]: value
              },
              updatedAt: new Date().toISOString()
            }
          }),
        }))
      },
    }),
    {
      name: 'plot-storage',  // 既存のキー名を維持（重要！）
      version: 1,
      storage: createIndexedDBStorage('plot-storage'),  // IndexedDBを有効化（自動移行機能付き）
      partialize: (state) => ({ savedPlots: state.savedPlots }),  // データのみを保存
      migrate: (persistedState: any, version: number) => {
        // バージョン0（初期状態）からバージョン1への移行
        if (version === 0) {
          // 古いデータ構造から新しいデータ構造への変換
          return persistedState
        }
        return persistedState
      }
    }
  )
)

// プロットをテキスト形式にフォーマット
function formatPlotForExport(plot: SavedPlot): string {
  const { result, input } = plot
  const date = new Date(plot.createdAt).toLocaleString('ja-JP')
  const storyLength = input.storyLength === 'short' ? '短編' : 
                      input.storyLength === 'long' ? '長編' : '中編'
  
  let content = `【プロット情報】
生成日時: ${date}
タイトル: ${result.title}

【生成パラメータ】
ジャンル: ${input.genreCustom || input.genre}
舞台: ${input.stageCustom || input.stage}
物語の長さ: ${storyLength}
キーワード: ${result.keywords.join('、')}
奇抜さレベル: ${input.creativityLevel}/10

【前提】
${result.premise}

【あらすじ】
${result.synopsis}

【章構成】`

  if (result.structure && result.structure.length > 0) {
    result.structure.forEach((chapter) => {
      content += `\n第${chapter.number}章: ${chapter.title}
${chapter.summary}`
    })
  }

  if (result.characters && result.characters.length > 0) {
    content += '\n\n【主要登場人物】'
    result.characters.forEach((char) => {
      content += `\n${char.name}: ${char.role}`
      if (char.description) {
        content += `\n  ${char.description}`
      }
    })
  }

  if (result.themes && result.themes.length > 0) {
    content += `\n\n【テーマ】
${result.themes.join('、')}`
  }

  if (plot.memo) {
    content += `\n\n【メモ】
${plot.memo}`
  }

  if (plot.tags && plot.tags.length > 0) {
    content += `\n\n【タグ】
${plot.tags.join('、')}`
  }

  return content
}

// テキストファイルをダウンロード
function downloadTextFile(content: string, filename: string) {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

// JSONファイルをダウンロード
function downloadJSONFile(data: any, filename: string) {
  const jsonString = JSON.stringify(data, null, 2)
  const blob = new Blob([jsonString], { type: 'application/json;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}